import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { ArrowRight, ArrowUpRight, Info } from "@phosphor-icons/react";
import { EASE_OUT, MaskWords, PageFrame, Photo, Seo, useMediaQuery } from "../../kit.jsx";
import { MEETINGS } from "./data.js";
import "./stories.css";

function StatusLabel({ meeting }) {
  return (
    <span className={`st-status ${meeting.status === "Programme cohort" ? "st-status--cohort" : ""}`}>
      <span className="st-status__key">Status</span>
      <span>{meeting.status}</span>
    </span>
  );
}

function Stage({ meeting }) {
  const reduce = useReducedMotion();
  return (
    <div className="st-ledger__stage" aria-hidden="true">
      <AnimatePresence initial={false}>
        <motion.figure
          key={meeting.slug}
          className="st-ledger__stage-frame"
          initial={reduce ? false : { clipPath: "inset(100% 0% 0% 0% round 28px)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0% round 28px)" }}
          exit={reduce ? undefined : { clipPath: "inset(0% 0% 100% 0% round 28px)", transition: { duration: 0.6, ease: [0.65, 0, 0.35, 1] } }}
          transition={{ duration: 0.75, ease: [0.65, 0, 0.35, 1] }}
        >
          <Photo src={meeting.image} alt="" sizes="40vw" />
        </motion.figure>
      </AnimatePresence>
      <div className="st-ledger__stage-cap">
        <span className="st-mono">Record {meeting.number} of {MEETINGS.length}</span>
        <span>{meeting.name}</span>
      </div>
    </div>
  );
}

export default function MeetingsPage() {
  const reduce = useReducedMotion();
  const wide = useMediaQuery("(min-width: 1000px)");
  const [active, setActive] = useState(0);
  const rows = useRef([]);

  useEffect(() => {
    if (!wide) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(Number(entry.target.dataset.index));
        });
      },
      { rootMargin: "-45% 0px -50% 0px" },
    );
    rows.current.forEach((row) => row && observer.observe(row));
    return () => observer.disconnect();
  }, [wide]);

  return (
    <PageFrame className="rd-page st-page">
      <Seo
        title="Meeting Archive | Project GRID"
        path="/meetings"
        description="Meetings and visits connected to Project GRID, with the relationship named for each one."
      />
      <main className="st-meet">
        <header className="st-meet__head">
          <div>
            <p className="st-mono">People we've met</p>
            <MaskWords as="h1" text="Meetings, recorded as meetings." onMount delay={0.05} />
            <motion.p
              className="st-meet__lede"
              initial={reduce ? false : { opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.35, ease: EASE_OUT }}
            >
              Conversations and visits connected to GRID, each with a photograph, a date and what was discussed.
            </motion.p>
          </div>
          <motion.aside
            className="st-meet__note"
            aria-labelledby="st-meet-note-title"
            initial={reduce ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.5, ease: EASE_OUT }}
          >
            <Info weight="bold" aria-hidden="true" />
            <h2 id="st-meet-note-title">A meeting is not an endorsement.</h2>
            <p>A meeting is not an endorsement or a partnership. Each one carries a label that says what it is.</p>
          </motion.aside>
        </header>

        <section className="st-ledger" aria-label="Meeting and visit records">
          {wide && <Stage meeting={MEETINGS[active]} />}
          <ol className="st-ledger__rows">
            {MEETINGS.map((meeting, index) => (
              <li
                key={meeting.slug}
                ref={(node) => { rows.current[index] = node; }}
                data-index={index}
                className={wide && active === index ? "is-active" : undefined}
              >
                <Link to={`/meetings/${meeting.slug}`} className="st-ledger__row">
                  <span className="st-ledger__date st-mono">{meeting.date}</span>
                  {!wide && (
                    <span className="st-ledger__thumb">
                      <Photo src={meeting.image} alt={meeting.alt} sizes="100vw" />
                    </span>
                  )}
                  <span className="st-ledger__name">{meeting.name}</span>
                  <span className="st-ledger__role">{meeting.role}</span>
                  <span className="st-ledger__context">{meeting.context}</span>
                  <span className="st-ledger__foot">
                    <StatusLabel meeting={meeting} />
                    <span className="st-ledger__open">Open the record <ArrowUpRight weight="bold" aria-hidden="true" /></span>
                  </span>
                </Link>
              </li>
            ))}
          </ol>
        </section>

        <section className="st-meet__end" aria-labelledby="st-meet-end-title">
          <h2 id="st-meet-end-title">Looking for our partners?</h2>
          <p>Partners are listed separately, under the programme they belong to, with the relationship named.</p>
          <div className="gh-actions">
            <Link className="gh-btn gh-btn--fill" to="/partnerships">
              <span className="roll" data-label="See partners"><span>See partners</span></span>
              <ArrowRight weight="bold" aria-hidden="true" />
            </Link>
          </div>
        </section>
      </main>
    </PageFrame>
  );
}
